import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Envelope, Eye, EyeSlash, Lock, SignIn, SpinnerGap } from '@phosphor-icons/react';
import { adminAuthAPI, internshipAPI } from '../../services/api';
import BrandLockup from '../BrandLockup';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = 'Sign in | Growmore IT Services';
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get('email') || '').trim();
    const password = String(formData.get('password') || '');

    try {
      try {
        const { data } = await internshipAPI.login({ email, password });
        if (data?.token) {
          localStorage.setItem('intern_token', data.token);
          navigate('/intern/portal');
          return;
        }
      } catch {
        /* try admin next */
      }

      try {
        const { data } = await adminAuthAPI.login({ email, password });
        if (data?.token) {
          localStorage.setItem('admin_token', data.token);
          localStorage.setItem('admin_user', JSON.stringify(data.admin));
          navigate('/admin/interns');
          return;
        }
      } catch {
        /* fall through */
      }

      setError('Could not sign in. Check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-50 flex items-center justify-center px-4 py-6 sm:py-10 [&_.input-field]:!bg-white [&_.input-field]:!text-gray-900 [&_.input-field]:!border-gray-200">
      <div className="pointer-events-none absolute -top-24 -left-10 w-80 h-80 rounded-full bg-sky-200/50 blur-3xl login-float" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[26rem] h-[26rem] rounded-full bg-indigo-200/40 blur-3xl login-float-slow" />
      <div className="pointer-events-none absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-brand-100/70 blur-2xl login-float" />

      <Link
        to="/"
        className="absolute top-[max(1rem,env(safe-area-inset-top))] left-4 z-10 inline-flex items-center gap-2 px-3 py-2 sm:px-5 sm:py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 shadow-sm hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50 smooth-transition"
      >
        <ArrowLeft className="w-4 h-4" weight="bold" />
        Back to website
      </Link>

      <div className="relative w-full max-w-[440px]">
        <form
          onSubmit={onSubmit}
          className="login-card-enter bg-white rounded-3xl border border-gray-100 shadow-xl px-5 py-6 sm:px-8 sm:py-8"
        >
          <div className="flex flex-col items-center mb-6">
            <BrandLockup as="div" size="md" className="shrink-0" />
            <h1 className="mt-4 font-display text-xl font-extrabold text-gray-900">Sign in</h1>
          </div>

          <div className="relative mb-3">
            <Envelope className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none shrink-0" />
            <input
              name="email"
              type="email"
              required
              autoComplete="username"
              className="input-field !pl-12 text-base"
              placeholder="Email"
              aria-label="Email"
            />
          </div>

          <div className="relative mb-4">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none shrink-0" />
            <input
              name="password"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              className="input-field !pl-12 !pr-12 text-base"
              placeholder="Password"
              aria-label="Password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50 smooth-transition"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <p className="text-xs text-gray-500 mb-4">
            <Link to="/intern/forgot" className="font-semibold text-brand-600 hover:text-brand-700">
              Forgot password?
            </Link>
          </p>

          {error && (
            <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary text-white w-full inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:hover:scale-100 group relative overflow-hidden"
          >
            <span className="relative z-10 inline-flex items-center gap-2">
              {loading ? <SpinnerGap className="w-5 h-5 animate-spin" /> : <SignIn className="w-5 h-5" weight="bold" />}
              Sign in
            </span>
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
